/**
 * DomesticHeatPumpModelsAdmin — admin-only page for the Sun Stellar
 * Domestic Heat Pump (Water Heating Selector) Calculator's SKU catalogue
 * (Calculator #4).
 *
 * Mirrors the structure of CommercialHeatPumpModelsAdmin / SsTankModelsAdmin:
 * physical product specs (kW, tank L, COP, max members/washrooms/sqft) are
 * read-only — they ship from Sun Stellar Engineering and only Engineering
 * changes them (engine semver bumps when they do). The only field this
 * page mutates is the per-SKU stock status.
 *
 * Per docs/calculators/domestic-heat-pump/REFERENCE.html the catalogue
 * contains 6 SKUs split across 3 outcome buckets:
 *   - Standard (4):       M2 5kW/300L · M3 7kW/300L · M4 7kW/500L · M5 11kW/500L
 *   - Compact (1):        M1 5kW/200L Hot & Cold (very small families)
 *   - Budget (1):         M6 5kW/300L Heating-only (no cooling)
 *
 * Plus 3 series-installation pre-sets (S1/S2/S3) which combine two SKUs in
 * series for high-occupancy or multi-jacuzzi homes. The series matrix is
 * read-only on this page — it's logic, not an SKU you stock.
 *
 * Catalog source: backend/src/modules/sun-stellar-domestic-heat-pump-calc/
 * catalog.ts. The page does NOT pull from a DB table — it pulls from the
 * backend's GET /v1/domestic-heat-pump/catalog/products endpoint (DHP-B
 * owns this). Until that endpoint ships, the table renders the in-code
 * values and the stock toggle calls
 *   PATCH /v1/domestic-heat-pump/catalog/products/:model
 * If the endpoint is not yet wired, the toggle surfaces a friendly error
 * but the visual state still flips locally so QA can preview UX.
 *
 * Track D — Calculator #4 (DHP-E, May 2026).
 *
 * TODO (parent integration): add `<Route path="/admin/domestic-heat-pump-models"
 *   element={<DomesticHeatPumpModelsAdmin />} />` to App.tsx (next to the
 *   existing `/admin/pool-models`, `/admin/commercial-heat-pump-models`,
 *   and `/admin/ss-tank-models` routes) once DHP-A/B/C land. DHP-C owns
 *   the App.tsx edit — leaving it alone here.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertTriangle, Check, Info } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';

type StockStatus = 'available' | 'low_stock' | 'made_to_order';

interface CatalogProductRow {
  /** SKU display code, e.g. "M1", "M2" — natural ID from REFERENCE.html. */
  model: string;
  /** Heating capacity (kW). */
  kw: number;
  /** Tank size (litres). */
  tank_l: number;
  /** Display name, e.g. "5 kW Hot & Cold — 200 L Tank". */
  name: string;
  /** Series label — Compact / Standard / Premium / Premium Plus / Budget. */
  series: string;
  /** Output type — hotcold | heatonly. */
  type: 'hotcold' | 'heatonly';
  /** Max coverable area (sq ft). */
  max_sqft: number;
  /** Max family members supported. */
  max_members: number;
  /** Max washrooms supported. */
  max_washrooms: number;
  /** Max staff supported. */
  max_staff: number;
  /** Kitchen support — true / false / 'conditional' (≤ 4 members). */
  kitchen_ok: boolean | 'conditional';
  /** Reverse-line / circulation pump support. */
  reverse_line_ok: boolean;
  /** Jacuzzi / bath-tub support — true / false / 'conditional' (≤ 5 members). */
  jacuzzi_ok: boolean | 'conditional';
  /** High daily-usage (body jets / large showers) supported. */
  high_usage_ok: boolean;
  /** Optional INR pricing — wired post-launch per §11.4. */
  price_inr?: number | null;
  /** Admin-controlled stock status. */
  stock_status: StockStatus;
}

interface SeriesPresetRow {
  /** Series key — S1 / S2 / S3. */
  key: 'S1' | 'S2' | 'S3';
  /** Display label, e.g. "7 kW 300 L + 5 kW 300 L in Series". */
  label: string;
  /** First SKU model code. */
  m1_model: string;
  /** Second SKU model code. */
  m2_model: string;
  /** Combined kW. */
  total_kw: number;
  /** Combined tank L. */
  total_tank_l: number;
  /** When-to-use rule of thumb. */
  when: string;
}

interface CatalogResponse {
  /** All single-SKU rows (admin can flip stock status per row). */
  models: CatalogProductRow[];
  /** Series installation pre-sets — read-only, derived from models. */
  series_presets: SeriesPresetRow[];
}

// ─── In-code fallback (mirrors backend/src/modules/.../catalog.ts) ─────
// Used only if the GET /catalog/products endpoint is not yet wired by DHP-B.
// Numbers MUST stay in sync with REFERENCE.html `MODELS` array (see lines
// 355–362 of docs/calculators/domestic-heat-pump/REFERENCE.html). If you
// change one here, change the other; otherwise the page lies about what
// the engine does.
const FALLBACK_CATALOG: CatalogResponse = {
  models: [
    {
      model: 'M1',
      kw: 5,
      tank_l: 200,
      name: '5 kW Hot & Cold — 200 L Tank',
      series: 'Compact',
      type: 'hotcold',
      max_sqft: 3000,
      max_members: 3,
      max_washrooms: 2,
      max_staff: 0,
      kitchen_ok: false,
      reverse_line_ok: true,
      jacuzzi_ok: false,
      high_usage_ok: false,
      price_inr: null,
      stock_status: 'available',
    },
    {
      model: 'M2',
      kw: 5,
      tank_l: 300,
      name: '5 kW Hot & Cold — 300 L Tank',
      series: 'Standard',
      type: 'hotcold',
      max_sqft: 5000,
      max_members: 4,
      max_washrooms: 4,
      max_staff: 0,
      kitchen_ok: false,
      reverse_line_ok: false,
      jacuzzi_ok: false,
      high_usage_ok: false,
      price_inr: null,
      stock_status: 'available',
    },
    {
      model: 'M3',
      kw: 7,
      tank_l: 300,
      name: '7 kW Hot & Cold — 300 L Tank',
      series: 'Standard',
      type: 'hotcold',
      max_sqft: 6000,
      max_members: 5,
      max_washrooms: 5,
      max_staff: 0,
      kitchen_ok: 'conditional',
      reverse_line_ok: true,
      jacuzzi_ok: false,
      high_usage_ok: false,
      price_inr: null,
      stock_status: 'available',
    },
    {
      model: 'M4',
      kw: 7,
      tank_l: 500,
      name: '7 kW Hot & Cold — 500 L Tank',
      series: 'Premium',
      type: 'hotcold',
      max_sqft: 7500,
      max_members: 6,
      max_washrooms: 6,
      max_staff: 2,
      kitchen_ok: true,
      reverse_line_ok: true,
      jacuzzi_ok: 'conditional',
      high_usage_ok: false,
      price_inr: null,
      stock_status: 'available',
    },
    {
      model: 'M5',
      kw: 11,
      tank_l: 500,
      name: '11 kW Hot & Cold — 500 L Tank',
      series: 'Premium Plus',
      type: 'hotcold',
      max_sqft: 9000,
      max_members: 7,
      max_washrooms: 7,
      max_staff: 2,
      kitchen_ok: true,
      reverse_line_ok: true,
      jacuzzi_ok: 'conditional',
      high_usage_ok: false,
      price_inr: null,
      stock_status: 'available',
    },
    {
      model: 'M6',
      kw: 5,
      tank_l: 300,
      name: '5 kW Heating Only — 300 L Tank',
      series: 'Budget',
      type: 'heatonly',
      max_sqft: 4000,
      max_members: 5,
      max_washrooms: 4,
      max_staff: 0,
      kitchen_ok: true,
      reverse_line_ok: false,
      jacuzzi_ok: false,
      high_usage_ok: false,
      price_inr: null,
      stock_status: 'available',
    },
  ],
  series_presets: [
    {
      key: 'S1',
      label: '7 kW 300 L + 5 kW 300 L in Series',
      m1_model: 'M3',
      m2_model: 'M2',
      total_kw: 12,
      total_tank_l: 600,
      when: 'Effective occupancy up to 6 — with jacuzzi, reverse line and kitchen.',
    },
    {
      key: 'S2',
      label: '7 kW 500 L + 5 kW 300 L in Series',
      m1_model: 'M4',
      m2_model: 'M2',
      total_kw: 12,
      total_tank_l: 800,
      when: 'Effective occupancy above 6 — larger family or family with staff.',
    },
    {
      key: 'S3',
      label: '11 kW 500 L + 7 kW 500 L in Series',
      m1_model: 'M5',
      m2_model: 'M4',
      total_kw: 18,
      total_tank_l: 1000,
      when:
        'Bungalow above 11,000 sq ft, or effective occupancy ≥ 8 with jacuzzi, kitchen and pantry.',
    },
  ],
};

const STOCK_OPTIONS: { value: StockStatus; label: string; bg: string; fg: string }[] = [
  { value: 'available',     label: 'IN STOCK',      bg: '#d8f5d8', fg: '#065c0c' },
  { value: 'low_stock',     label: 'LOW STOCK',     bg: '#fff3cd', fg: '#7a5800' },
  { value: 'made_to_order', label: 'MADE TO ORDER', bg: '#fde7d8', fg: '#8a3a08' },
];

type TabKey = 'standard' | 'compact' | 'budget' | 'series';

const TAB_LABELS: Record<TabKey, { title: string; subtitle: string }> = {
  standard: {
    title: 'Standard / Premium',
    subtitle: '4 SKUs · M2–M5 · Hot & Cold · 5–11 kW · 300–500 L',
  },
  compact: {
    title: 'Compact',
    subtitle: '1 SKU · M1 · Hot & Cold · 5 kW · 200 L · very small families',
  },
  budget: {
    title: 'Budget (heating only)',
    subtitle: '1 SKU · M6 · heating only — no cooling output',
  },
  series: {
    title: 'Series presets',
    subtitle: '3 presets · two-SKU series installs · read-only · derived from SKUs',
  },
};

/** Render a tri-state support flag. */
function flagText(v: boolean | 'conditional'): string {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return 'Conditional';
}

/** Render a tri-state support flag with a colored pill. */
function flagPill(v: boolean | 'conditional'): React.CSSProperties {
  if (v === true) return { background: '#d8f5d8', color: '#065c0c' };
  if (v === false) return { background: '#fde7e7', color: '#8b2222' };
  return { background: '#fff3cd', color: '#7a5800' };
}

export function DomesticHeatPumpModelsAdmin() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = (user?.role?.name ?? '').toLowerCase() === 'admin';

  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [savingModel, setSavingModel] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('standard');

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    api
      .get<CatalogResponse>('/domestic-heat-pump/catalog/products')
      .then((r) => setCatalog(r.data))
      .catch((e) => {
        // If the endpoint isn't wired yet (404 or network), fall back to the
        // in-code catalogue so the page is still useful for QA. The toggle
        // will show a friendly error when actually clicked.
        const status = e?.response?.status;
        if (status === 404 || status === undefined) {
          setCatalog(FALLBACK_CATALOG);
          setWarning(
            'Backend catalog endpoint not yet wired. Showing in-code values. ' +
              'Stock toggle will fail until DHP-B ships GET/PATCH ' +
              '/domestic-heat-pump/catalog/products.'
          );
        } else {
          setError(e?.response?.data?.error ?? 'Could not load catalog.');
        }
      })
      .finally(() => setLoading(false));
  }, [isAdmin]);

  async function setStock(model: string, next: StockStatus) {
    if (!catalog) return;
    setSavingModel(model);
    setError(null);
    try {
      const res = await api.patch<CatalogProductRow>(
        `/domestic-heat-pump/catalog/products/${encodeURIComponent(model)}`,
        { stock_status: next }
      );
      const updated: CatalogProductRow = res.data;
      setCatalog((prev) => prev && {
        ...prev,
        models: prev.models.map((r) => (r.model === model ? updated : r)),
      });
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 404 || status === undefined) {
        // Endpoint not yet implemented. Flip locally so QA can preview UX.
        setCatalog((prev) => prev && {
          ...prev,
          models: prev.models.map((r) =>
            r.model === model ? { ...r, stock_status: next } : r
          ),
        });
        setError(
          `PATCH /domestic-heat-pump/catalog/products/${model} not yet wired. ` +
            'Local state updated for preview only — refresh will revert.'
        );
      } else {
        setError(e?.response?.data?.error ?? 'Could not update stock status.');
      }
    } finally {
      setSavingModel(null);
    }
  }

  const allModels: CatalogProductRow[] = useMemo(() => catalog?.models ?? [], [catalog]);
  const seriesRows: SeriesPresetRow[] = useMemo(() => catalog?.series_presets ?? [], [catalog]);

  // Bucket models per tab. Standard/Premium tab covers M2-M5 (the 4 mainline
  // SKUs); Compact is M1; Budget is M6.
  const tabRows: CatalogProductRow[] = useMemo(() => {
    switch (tab) {
      case 'standard':
        return allModels.filter((m) =>
          ['Standard', 'Premium', 'Premium Plus'].includes(m.series)
        );
      case 'compact':
        return allModels.filter((m) => m.series === 'Compact');
      case 'budget':
        return allModels.filter((m) => m.series === 'Budget');
      default:
        return [];
    }
  }, [allModels, tab]);

  const tabCount: Record<TabKey, number> = useMemo(() => ({
    standard: allModels.filter((m) =>
      ['Standard', 'Premium', 'Premium Plus'].includes(m.series)
    ).length,
    compact:  allModels.filter((m) => m.series === 'Compact').length,
    budget:   allModels.filter((m) => m.series === 'Budget').length,
    series:   seriesRows.length,
  }), [allModels, seriesRows]);

  if (!isAdmin) {
    return (
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 20px' }}>
        <h1 style={{ fontFamily: '"Fraunces", serif', fontSize: 28, fontWeight: 600, color: '#003a6b', margin: 0 }}>
          Domestic Heat Pump Catalog
        </h1>
        <div style={emptyStateBox}>Admin only.</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontFamily: '"Fraunces", serif', fontSize: 28, fontWeight: 600, color: '#003a6b', margin: 0 }}>
          Domestic Heat Pump Catalog
        </h1>
        <span style={catalogSourcePill}>
          <Info size={12} /> Catalog source: in-code (catalog.ts)
        </span>
      </div>

      <p style={{ color: '#666', marginTop: 4, lineHeight: 1.5 }}>
        Domestic heat pump catalogue (water heating) for the Sun Stellar Domestic
        Heat Pump Selector. Capacities (kW, tank L), area / member / washroom
        envelopes, and feature support (kitchen, reverse line, jacuzzi) are
        physical product specs and ship from Sun Stellar Engineering — they're
        read-only here. The only editable field is per-SKU stock status; flip it
        as inventory changes and the engine will recommend the next-best SKU or
        a series install when a primary recommendation is out of stock. Pricing
        is wired post-launch (per §11.4 — applied at quote generation).
      </p>

      {warning && (
        <div style={{ ...alertBox, background: '#fff8e1', color: '#7a5800' }}>
          <AlertTriangle size={16} /> {warning}
        </div>
      )}

      {error && (
        <div style={{ ...alertBox, background: '#fde7e7', color: '#8b2222' }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Tabs */}
      <div role="tablist" style={tabRow}>
        {(Object.keys(TAB_LABELS) as TabKey[]).map((key) => {
          const meta = TAB_LABELS[key];
          const count = tabCount[key];
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(key)}
              style={{
                ...tabButton,
                background: active ? '#003a6b' : '#f3f8ff',
                color: active ? '#fff' : '#003a6b',
                borderBottomColor: active ? '#003a6b' : 'transparent',
              }}
            >
              <span style={{ fontWeight: 600 }}>{meta.title}</span>
              <span style={{ fontSize: 11, opacity: 0.85 }}>({count})</span>
            </button>
          );
        })}
      </div>

      <p style={{ color: '#777', fontSize: 12, marginTop: 4, marginBottom: 12 }}>
        {TAB_LABELS[tab].subtitle}
      </p>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Loader2 className="animate-spin" />
        </div>
      ) : tab === 'series' ? (
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#f3f8ff', textAlign: 'left' }}>
              <th style={th}>Preset</th>
              <th style={th}>Configuration</th>
              <th style={th}>HP1</th>
              <th style={th}>HP2</th>
              <th style={th}>Total kW</th>
              <th style={th}>Total tank (L)</th>
              <th style={th}>When to use</th>
            </tr>
          </thead>
          <tbody>
            {seriesRows.map((r) => (
              <tr key={r.key} style={{ borderBottom: '1px solid #eee' }}>
                <td style={td}><strong>{r.key}</strong></td>
                <td style={td}>{r.label}</td>
                <td style={td}><code style={codeChip}>{r.m1_model}</code></td>
                <td style={td}><code style={codeChip}>{r.m2_model}</code></td>
                <td style={td}>{r.total_kw}</td>
                <td style={td}>{r.total_tank_l.toLocaleString('en-IN')}</td>
                <td style={{ ...td, color: '#555', fontSize: 12 }}>{r.when}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#f3f8ff', textAlign: 'left' }}>
              <th style={th}>Model</th>
              <th style={th}>Name</th>
              <th style={th}>kW</th>
              <th style={th}>Tank (L)</th>
              <th style={th}>Max sq ft</th>
              <th style={th}>Max members</th>
              <th style={th}>Max washrooms</th>
              <th style={th}>Kitchen</th>
              <th style={th}>Rev. line</th>
              <th style={th}>Jacuzzi</th>
              <th style={th}>Price (INR)</th>
              <th style={th}>Stock status</th>
            </tr>
          </thead>
          <tbody>
            {tabRows.map((r) => {
              const isSaving = savingModel === r.model;
              const opt = STOCK_OPTIONS.find((s) => s.value === r.stock_status) ?? STOCK_OPTIONS[0]!;
              return (
                <tr key={r.model} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={td}><strong>{r.model}</strong></td>
                  <td style={{ ...td, fontSize: 12, color: '#555' }}>{r.name}</td>
                  <td style={td}>{r.kw}</td>
                  <td style={td}>{r.tank_l.toLocaleString('en-IN')}</td>
                  <td style={td}>{r.max_sqft.toLocaleString('en-IN')}</td>
                  <td style={td}>{r.max_members}</td>
                  <td style={td}>{r.max_washrooms}</td>
                  <td style={td}>
                    <span style={{ ...flagBadge, ...flagPill(r.kitchen_ok) }}>{flagText(r.kitchen_ok)}</span>
                  </td>
                  <td style={td}>
                    <span style={{ ...flagBadge, ...flagPill(r.reverse_line_ok) }}>{flagText(r.reverse_line_ok)}</span>
                  </td>
                  <td style={td}>
                    <span style={{ ...flagBadge, ...flagPill(r.jacuzzi_ok) }}>{flagText(r.jacuzzi_ok)}</span>
                  </td>
                  <td style={{ ...td, color: '#999', fontStyle: 'italic', fontSize: 12 }}>
                    Wired post-launch
                  </td>
                  <td style={td}>
                    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                      <select
                        value={r.stock_status}
                        onChange={(e) => setStock(r.model, e.target.value as StockStatus)}
                        disabled={isSaving}
                        aria-label={`Stock status for ${r.model}`}
                        style={{
                          padding: '4px 8px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 600,
                          border: 0,
                          cursor: isSaving ? 'wait' : 'pointer',
                          background: opt.bg,
                          color: opt.fg,
                        }}
                      >
                        {STOCK_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      {isSaving ? (
                        <Loader2 size={12} className="animate-spin" color="#777" />
                      ) : (
                        <Check size={12} color="#999" />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────
const th: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '2px solid #ddd',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13 };
const tableStyle: React.CSSProperties = {
  width: '100%',
  marginTop: 8,
  borderCollapse: 'collapse',
};
const tabRow: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  marginTop: 16,
  borderBottom: '1px solid #ddd',
  flexWrap: 'wrap',
};
const tabButton: React.CSSProperties = {
  padding: '8px 16px',
  border: 0,
  borderBottom: '3px solid transparent',
  borderRadius: '6px 6px 0 0',
  cursor: 'pointer',
  fontSize: 13,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  transition: 'background 0.15s',
};
const alertBox: React.CSSProperties = {
  padding: 12,
  borderRadius: 6,
  marginTop: 12,
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  fontSize: 13,
  lineHeight: 1.4,
};
const catalogSourcePill: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: '#eef4fb',
  color: '#003a6b',
  fontSize: 11,
  fontWeight: 500,
  padding: '4px 10px',
  borderRadius: 999,
};
const emptyStateBox: React.CSSProperties = {
  marginTop: 32,
  padding: 32,
  background: '#f7f7f7',
  border: '1px dashed #ccc',
  borderRadius: 8,
  textAlign: 'center',
  color: '#666',
};
const flagBadge: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
};
const codeChip: React.CSSProperties = {
  padding: '2px 6px',
  background: '#f3f8ff',
  borderRadius: 4,
  fontSize: 12,
  color: '#003a6b',
  fontFamily: 'monospace',
};
